import type {
  Consumer,
  ConsumerCreated,
  ConsumerEventList,
  ConsumerList,
  CreateConsumer,
  Product,
  ProductList,
  UpdateConsumer,
  UpdateProduct,
} from '@easyhelp/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, apiRequest } from './client';

/** Chiavi delle query del registro: consumer, prodotti ed eventi di un consumer. */
export const consumersQueryKey = ['consumers'] as const;
export const productsQueryKey = ['products'] as const;
export const consumerEventsQueryKey = (id: string) => ['consumers', id, 'events'] as const;

/** `GET /consumers`: il registro con l'ambiente dell'istanza. */
export function fetchConsumers(): Promise<ConsumerList> {
  return apiRequest<ConsumerList>('/consumers');
}

/** Registro dei consumer, dal più recente. */
export function useConsumers() {
  return useQuery({ queryKey: consumersQueryKey, queryFn: fetchConsumers });
}

/** Prodotti con prefisso, blocco del prefisso e consumer attivi. */
export function useProducts() {
  return useQuery({ queryKey: productsQueryKey, queryFn: () => apiRequest<ProductList>('/products') });
}

/** Storico delle operazioni su un consumer, dal più recente. */
export function useConsumerEvents(id: string) {
  return useQuery({
    queryKey: consumerEventsQueryKey(id),
    queryFn: () => apiRequest<ConsumerEventList>(`/consumers/${id}/events`),
  });
}

/** Invalida registro e prodotti: ordine, conteggi e prefissi li decide l'API. */
function useInvalidateRegistry() {
  const queryClient = useQueryClient();
  return () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: consumersQueryKey }),
      queryClient.invalidateQueries({ queryKey: productsQueryKey }),
    ]);
}

/**
 * `POST /consumers`: crea il consumer (e il prodotto, se nuovo); il secret è **solo** in questa
 * risposta. `gcTime: 0`: dopo il `reset()` la risposta con il secret non resta nella cache delle mutation.
 */
export function useCreateConsumer() {
  const invalidate = useInvalidateRegistry();
  return useMutation<ConsumerCreated, ApiError, CreateConsumer>({
    gcTime: 0,
    mutationFn: (input) => apiRequest<ConsumerCreated>('/consumers', { method: 'POST', body: JSON.stringify(input) }),
    onSuccess: invalidate,
  });
}

/** `PATCH /consumers/:id`: nome e committenti autorizzati. */
export function useUpdateConsumer() {
  const invalidate = useInvalidateRegistry();
  return useMutation<Consumer, ApiError, { id: string; changes: UpdateConsumer }>({
    mutationFn: ({ id, changes }) =>
      apiRequest<Consumer>(`/consumers/${id}`, { method: 'PATCH', body: JSON.stringify(changes) }),
    onSuccess: invalidate,
  });
}

/** `POST /consumers/:id/revoke`: revoca definitiva (409 se già revocato). */
export function useRevokeConsumer() {
  const invalidate = useInvalidateRegistry();
  return useMutation<Consumer, ApiError, string>({
    mutationFn: (id) => apiRequest<Consumer>(`/consumers/${id}/revoke`, { method: 'POST' }),
    onSuccess: invalidate,
  });
}

/** `PATCH /products/:id`: nome e, finché non ci sono ticket, prefisso. */
export function useUpdateProduct() {
  const invalidate = useInvalidateRegistry();
  return useMutation<Product, ApiError, { id: string; changes: UpdateProduct }>({
    mutationFn: ({ id, changes }) =>
      apiRequest<Product>(`/products/${id}`, { method: 'PATCH', body: JSON.stringify(changes) }),
    onSuccess: invalidate,
  });
}
